import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Search, BookOpen, Clock, CheckCircle, Shield, Sparkles } from 'lucide-react';
import Header from './Header';

// Using relative URLs

const TrainingList = () => {
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [frameworkFilter, setFrameworkFilter] = useState('all');
  const [stats, setStats] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('archive');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  useEffect(() => {
    fetchTrainings();
    fetchStats();
  }, []);

  useEffect(() => {
    filterTrainings();
  }, [trainings, searchQuery, statusFilter, frameworkFilter]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchTrainings = async () => {
    try {
      const response = await axios.get(
        `/api/trainings`,
        getAuthHeaders()
      );
      setTrainings(response.data || []);
    } catch (error) {
      // Don't show error toast - just set empty array
      // The UI will handle the empty state gracefully
      setTrainings([]);
      console.log('No trainings or error loading trainings:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `/api/trainings/compliance/stats`,
        getAuthHeaders()
      );
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const filterTrainings = () => {
    let filtered = [...trainings];

    if (searchQuery) {
      filtered = filtered.filter(training =>
        training.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (training.description && training.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(training => training.status === statusFilter);
    }

    if (frameworkFilter !== 'all') {
      filtered = filtered.filter(training => 
        training.frameworks && 
        Array.isArray(training.frameworks) && 
        training.frameworks.includes(frameworkFilter)
      );
    }

    setFilteredTrainings(filtered);
  };

  const calculateFrameworkStats = () => {
    const frameworkCount = {};
    trainings.forEach(training => {
      if (training.status === 'archived') return; // exclude archived
      if (training.frameworks && Array.isArray(training.frameworks)) {
        training.frameworks.forEach(framework => {
          frameworkCount[framework] = (frameworkCount[framework] || 0) + 1;
        });
      }
    });
    return frameworkCount;
  };

  const calculateStatusStats = () => {
    const statusCount = {
      draft: 0,
      published: 0,
      archived: 0
    };
    trainings.forEach(training => {
      if (statusCount[training.status] !== undefined) {
        statusCount[training.status]++;
      }
    });
    return statusCount;
  };

  const calculateAverageDuration = () => {
    const activeTrainings = trainings.filter(t => t.status !== 'archived');
    if (activeTrainings.length === 0) return 0;
    const total = activeTrainings.reduce((sum, training) => sum + (training.duration_minutes || 0), 0);
    return Math.round(total / activeTrainings.length);
  };

  const calculateTotalQuestions = () => {
    return trainings.reduce((sum, training) => {
      if (training.status === 'archived') return sum;
      const questions = training.questions || [];
      return sum + questions.length;
    }, 0);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'archived':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Reset selection when filters change
  useEffect(() => {
    setSelectedIds([]);
  }, [statusFilter, frameworkFilter, searchQuery]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const viewingArchived = statusFilter === 'archived';
  const selectableIds = filteredTrainings
    .filter(t => viewingArchived ? t.status === 'archived' : t.status !== 'archived')
    .map(t => t.id);

  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableIds);
    }
  };

  const openBulkDialog = (action) => {
    setBulkAction(action);
    setBulkDialogOpen(true);
  };

  const handleBulkConfirm = async () => {
    if (selectedIds.length === 0) return;
    setBulkProcessing(true);
    try {
      const endpoint = bulkAction === 'archive' ? 'bulk-archive' : 'bulk-unarchive';
      const response = await axios.post(`/api/trainings/${endpoint}`, { ids: selectedIds }, getAuthHeaders());
      const count = bulkAction === 'archive'
        ? response.data.archived_count
        : response.data.unarchived_count;
      await fetchTrainings();
      toast.success(`${count} training(s) ${bulkAction === 'archive' ? 'archived' : 'unarchived'}`);
      setSelectedIds([]);
      setBulkDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${bulkAction} trainings`);
    } finally {
      setBulkProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Training Management</h1>
          <p className="text-slate-600">Create and manage compliance training modules</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Trainings</CardDescription>
              <CardTitle className="text-3xl">{trainings.filter(t => t.status !== 'archived').length}</CardTitle>
              <div className="text-xs text-slate-500 mt-2">
                {(() => {
                  const statusStats = calculateStatusStats();
                  return `${statusStats.draft} draft, ${statusStats.published} published, ${statusStats.archived} archived`;
                })()}
              </div>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Published Trainings</CardDescription>
              <CardTitle className="text-3xl text-navy-600">
                {calculateStatusStats().published}
              </CardTitle>
              <div className="text-xs text-slate-500 mt-2">Active for compliance</div>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Average Duration</CardDescription>
              <CardTitle className="text-3xl">{calculateAverageDuration()} min</CardTitle>
              <div className="text-xs text-slate-500 mt-2">Per training module</div>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Questions</CardDescription>
              <CardTitle className="text-3xl">{calculateTotalQuestions()}</CardTitle>
              <div className="text-xs text-slate-500 mt-2">Across all trainings</div>
            </CardHeader>
          </Card>
          
          {stats ? (
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Completion Rate</CardDescription>
                <CardTitle className="text-3xl text-navy-600">{stats.completion_percentage}%</CardTitle>
                <div className="text-xs text-slate-500 mt-2">Employee progress</div>
              </CardHeader>
            </Card>
          ) : (
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Draft Trainings</CardDescription>
                <CardTitle className="text-3xl text-yellow-600">
                  {calculateStatusStats().draft}
                </CardTitle>
                <div className="text-xs text-slate-500 mt-2">In development</div>
              </CardHeader>
            </Card>
          )}
        </div>

        {/* Framework Breakdown */}
        {trainings.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Trainings by Framework</CardTitle>
                  <CardDescription>
                    Click on a framework to filter trainings. Trainings can belong to multiple frameworks.
                    {frameworkFilter !== 'all' && (
                      <span className="ml-2 text-navy-600 font-medium">
                        (Filtered by: {frameworkFilter})
                      </span>
                    )}
                  </CardDescription>
                </div>
                {frameworkFilter !== 'all' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFrameworkFilter('all')}
                    className="text-slate-600 hover:text-slate-900"
                  >
                    Clear Filter
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {/* All Frameworks Card */}
                <button
                  onClick={() => setFrameworkFilter('all')}
                  className={`rounded-lg p-4 border-2 transition-all duration-200 text-left ${
                    frameworkFilter === 'all'
                      ? 'bg-emerald-50 border-navy-500 shadow-md'
                      : 'bg-slate-50 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Shield className={`w-5 h-5 ${frameworkFilter === 'all' ? 'text-navy-600' : 'text-slate-400'}`} />
                    <span className={`text-2xl font-bold ${frameworkFilter === 'all' ? 'text-navy-600' : 'text-slate-900'}`}>
                      {trainings.filter(t => t.status !== 'archived').length}
                    </span>
                  </div>
                  <p className={`text-sm font-medium ${frameworkFilter === 'all' ? 'text-navy-700' : 'text-slate-700'}`}>
                    All Frameworks
                  </p>
                </button>

                {/* Individual Framework Cards */}
                {(() => {
                  const frameworkStats = calculateFrameworkStats();
                  return Object.keys(frameworkStats).length > 0 ? (
                    Object.entries(frameworkStats)
                      .sort((a, b) => b[1] - a[1])
                      .map(([framework, count]) => (
                        <button
                          key={framework}
                          onClick={() => setFrameworkFilter(framework)}
                          className={`rounded-lg p-4 border-2 transition-all duration-200 text-left ${
                            frameworkFilter === framework
                              ? 'bg-emerald-50 border-navy-500 shadow-md'
                              : 'bg-slate-50 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Shield className={`w-5 h-5 ${frameworkFilter === framework ? 'text-navy-600' : 'text-slate-400'}`} />
                            <span className={`text-2xl font-bold ${frameworkFilter === framework ? 'text-navy-600' : 'text-slate-900'}`}>
                              {count}
                            </span>
                          </div>
                          <p className={`text-sm font-medium ${frameworkFilter === framework ? 'text-navy-700' : 'text-slate-700'}`}>
                            {framework}
                          </p>
                        </button>
                      ))
                  ) : null;
                })()}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters and Actions */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
              <div className="flex-1 w-full lg:w-auto flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search trainings..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500"
                >
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  onClick={() => navigate('/trainings/wizard?mode=general')}
                  variant="outline"
                  className="border-navy-200 text-navy-700 hover:bg-navy-50"
                  data-testid="create-general-training-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Create General Training
                </Button>
                <Button
                  onClick={() => navigate('/trainings/wizard')}
                  className="bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white"
                  data-testid="create-training-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Training
                </Button>
              </div>
            </div>

            {/* Bulk Selection Bar */}
            {selectableIds.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200 flex flex-wrap items-center gap-3" data-testid="training-bulk-action-bar">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                    data-testid="training-select-all-checkbox"
                  />
                  <span className="text-sm text-slate-700">
                    {selectedIds.length > 0
                      ? `${selectedIds.length} selected`
                      : `Select all ${viewingArchived ? 'archived' : 'non-archived'} (${selectableIds.length})`}
                  </span>
                </label>
                {selectedIds.length > 0 && (
                  <>
                    {viewingArchived ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openBulkDialog('unarchive')}
                        className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        data-testid="training-bulk-unarchive-btn"
                      >
                        Unarchive Selected
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openBulkDialog('archive')}
                        className="text-amber-700 border-amber-300 hover:bg-amber-50"
                        data-testid="training-bulk-archive-btn"
                      >
                        Archive Selected
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedIds([])}
                      className="text-slate-500"
                      data-testid="training-bulk-clear-btn"
                    >
                      Clear
                    </Button>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trainings Grid */}
        {filteredTrainings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No trainings found</h3>
              <p className="text-slate-600 mb-6">
                {trainings.length === 0 
                  ? 'Get started by creating your first training'
                  : 'Try adjusting your search or filters'}
              </p>
              {trainings.length === 0 && (
                <Button
                  onClick={() => navigate('/trainings/wizard')}
                  className="bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Training
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTrainings.map((training) => (
              <Card 
                key={training.id} 
                className={`hover:shadow-lg transition-shadow cursor-pointer ${selectedIds.includes(training.id) ? 'ring-2 ring-navy-500' : ''}`}
                onClick={() => navigate(`/trainings/${training.id}`)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.includes(training.id)}
                          onCheckedChange={() => toggleSelect(training.id)}
                          data-testid={`training-select-${training.id}`}
                        />
                      </div>
                      <BookOpen className="w-6 h-6 text-navy-600" />
                    </div>
                    <Badge className={getStatusBadgeColor(training.status)}>
                      {training.status}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg">{training.title}</CardTitle>
                  {training.description && (
                    <CardDescription className="line-clamp-2">
                      {training.description}
                    </CardDescription>
                  )}
                </CardHeader>
                
                <CardContent>
                  <div className="space-y-3">
                    {/* Completion Stats */}
                    {training.completion_stats && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">
                              {training.completion_stats.completed_count} of {training.completion_stats.total_employees} completed
                            </span>
                          </div>
                          <Badge className="bg-blue-600 text-white text-xs">
                            {Math.round(training.completion_stats.completion_rate)}%
                          </Badge>
                        </div>
                      </div>
                    )}

                    {training.frameworks && training.frameworks.length > 0 && (
                      <div>
                        <p className="text-xs text-slate-500 mb-2">Frameworks:</p>
                        <div className="flex flex-wrap gap-1">
                          {training.frameworks.map((fw) => (
                            <Badge key={fw} variant="outline" className="text-xs">
                              {fw}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center text-sm text-slate-600">
                      <Clock className="w-4 h-4 mr-1" />
                      {training.duration_minutes} minutes
                    </div>

                    <div className="text-xs text-slate-500">
                      {training.questions?.length || 0} quiz questions
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Bulk Archive/Unarchive Dialog */}
      <AlertDialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <AlertDialogContent data-testid="training-bulk-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkAction === 'archive' ? 'Archive Selected Trainings?' : 'Unarchive Selected Trainings?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bulkAction === 'archive'
                ? `You are about to archive ${selectedIds.length} training(s). Archived trainings can be restored later.`
                : `You are about to unarchive ${selectedIds.length} training(s). They will be reset to Draft status.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="training-bulk-dialog-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkConfirm}
              disabled={bulkProcessing}
              data-testid="training-bulk-dialog-confirm"
            >
              {bulkProcessing
                ? 'Processing...'
                : bulkAction === 'archive' ? 'Archive' : 'Unarchive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TrainingList;
